import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { Box, Button, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { RECLASSIFIED_COLOR } from "@/shared/colors";
import { SawOutgoingBundle } from "../models/types";
import {
	type BundleReclassification,
	getBundleReclassification,
} from "../repositories/sawReclassifyRepository";
import {
	CardPaperSx,
	CardTitle,
	EMPTY,
	IdentityRows,
	InfoGrid,
	SAW_ACCENT,
	SawChip,
	StateChip,
	Unavailable,
} from "./primitives";
import { ReclassifyBundleModal } from "./ReclassifyBundleModal";

type Props = {
	data?: SawOutgoingBundle | null;
	assetId?: number | null;
	// FAB BundleId of the exiting bundle; null keeps Reclassify disabled.
	bundleId?: number | null;
	onReclassified?: () => void;
};

export const OutgoingBundleCard = ({
	data = null,
	assetId = null,
	bundleId = null,
	onReclassified,
}: Props) => {
	const [open, setOpen] = useState(false);

	const { data: reclassification, refetch } =
		useQuery<BundleReclassification | null>({
			queryKey: ["bundleReclassification", bundleId],
			queryFn: () => getBundleReclassification(bundleId as number),
			enabled: bundleId != null,
			meta: { realtime: ["FAB.GetBundleReclassification"] },
		});

	// The mark belongs to the exiting bundle only: the query is keyed by its id,
	// so a new exiting bundle never inherits the previous one's state.
	const stateBadge =
		bundleId != null && reclassification != null ? (
			reclassification.IsCutback ? (
				<StateChip label="Cutback" color="#757575" />
			) : (
				<StateChip label="Reclassified" color={RECLASSIFIED_COLOR} />
			)
		) : null;
	const reclassifiedTo =
		bundleId != null && reclassification != null && !reclassification.IsCutback
			? (reclassification.NewPartNumber ?? "?")
			: null;

	return (
		<Paper elevation={1} sx={CardPaperSx}>
			<CardTitle
				title="Outgoing Bundle"
				badge={stateBadge}
				action={
					<Button
						size="small"
						variant="contained"
						color="success"
						startIcon={<SwapHorizIcon />}
						disabled={assetId == null}
						onClick={() => setOpen(true)}
					>
						Reclassify Bundle
					</Button>
				}
			/>

			<Unavailable active={!data?.exitingBundleId && !data?.stack}>
				<IdentityRows
					accent={SAW_ACCENT}
					rows={[
						{
							label: "Exiting Bundle Id",
							value: data?.exitingBundleId,
							aside: <SawChip label={`${data?.stack ?? EMPTY} Stack`} />,
						},
					]}
				/>
			</Unavailable>

			<InfoGrid
				columns={1}
				items={[
					{
						label: "Total Pieces / Bundle",
						value: data?.totalPiecesPerBundle ?? null,
					},
				]}
			/>

			{reclassifiedTo != null && (
				<Box
					sx={{
						mt: 0.75,
						px: 1,
						py: 0.5,
						borderRadius: 1,
						border: `1px solid ${RECLASSIFIED_COLOR}33`,
						borderLeft: `3px solid ${RECLASSIFIED_COLOR}`,
						backgroundColor: `${RECLASSIFIED_COLOR}0D`,
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						minWidth: 0,
					}}
				>
					<Typography
						sx={{ fontSize: "0.78rem", color: "#5f6b7a", flexShrink: 0 }}
					>
						Reclassified
					</Typography>
					<Typography
						sx={{
							fontSize: "0.78rem",
							fontWeight: 700,
							color: "#1f2937",
							whiteSpace: "nowrap",
							overflow: "hidden",
							textOverflow: "ellipsis",
						}}
					>
						{reclassification?.OriginalPartNumber ?? "—"} → {reclassifiedTo}
					</Typography>
				</Box>
			)}

			<ReclassifyBundleModal
				open={open}
				assetId={assetId}
				bundleId={bundleId}
				onClose={() => setOpen(false)}
				onReclassified={() => {
					void refetch();
					onReclassified?.();
				}}
			/>
		</Paper>
	);
};
